import React, { useContext } from 'react';
import classNames from 'classnames';
import ResolveImage from 'components/ResolveImage';
import { Listing } from 'models/Listing';
import { Link } from 'react-router-dom';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import AuthContext from 'providers/Auth/Auth.context';
import StarIcon from 'icons/Star.icon';
import utils, {
  checkIsTierPremium,
  getIsListingOwner,
  getPortfolioRoute,
  getResolveUrl,
  isNewListing,
} from 'utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import ResolvePopup from 'components/ResolvePopup';
import StatusBadge from '../StatusBadge';
import TextBubleIcon from 'icons/TextBuble.icon';
import LinkToIcon from 'icons/LinkTo.icon';
import ShareButton from 'components/ShareButton';
import ListingPrices from 'components/ListingPrices';
import { getImgUrl } from 'components/ResolveImage/utils';
import useMatchMedia from 'hooks/useMatchMedia';
import useLocationMapper from 'router/data/useLocationMapper';
import useShowEnquireAndWatchlist from 'hooks/useShowEnquireAndWatchlist';
import useWatchlist from 'hooks/useWatchlist';

import './ListingCard.styles.scss';
import './ListingCard.styles.responsive.scss';

type ListingCardProps = {
  className?: string;
  listing: Listing;
  onWatchedListed: (value: boolean, listingId: string) => void;
  onEnquire: (listingId: string, redirectLink?: string) => void;
};

const ListingCard: React.FC<ListingCardProps> = (props) => {
  const { className, listing, onWatchedListed, onEnquire } = props;
  const { isLoggedIn } = useContext(AuthContext);
  const { t } = useTranslation();
  const isPhablet = useMatchMedia('isPhablet');

  const { currentUser } = useContext(CurrentUserContext);
  const currentUserId = currentUser?._id || '';

  const {
    _id: listingId,
    listingTitle,
    location,
    dealLocation,
    isWatchlisted,
    revenue,
    industry,
    gallery,
    userId,
    createdAt,
    slugName,
    businessDescription,
    EBITDA,
    averageStockValue,
    askingPriceSelect,
    stockAtValue,
    type,
    isEnquired,
    listingEnquiryId,
    dealRoomId,
    isCoOwner,
    currency,
    tier,
    listingContact,
  } = listing;

  const isListingOwner = getIsListingOwner(currentUserId, userId, isCoOwner);

  const classes = classNames('listing-card', className);
  const { locationLabelMapper } = useLocationMapper();

  const firmLogo = listingContact?.firmLogo?.url;

  const isListingPremium = checkIsTierPremium(tier);

  const redirectionListingLink = utils.composeBusinessUrl(
    industry,
    slugName,
    type,
  );

  const { showEnquire } = useShowEnquireAndWatchlist(listing);

  const { loading: watchlistRequestLoading, onWatchlist } = useWatchlist(
    listingId,
    dealRoomId,
    isWatchlisted,
    onWatchedListed,
  );

  return (
    <div className={classes}>
      <div className="listing-card__content">
        <div className="listing-card__content__top">
          <StatusBadge isNew={isNewListing(createdAt)} />
          <Link
            className="listing-card__content__top__title"
            to={redirectionListingLink}
          >
            {listingTitle}
          </Link>
          <Link
            to={redirectionListingLink}
            className="listing-card__content__top__listing-type"
          >
            <span>{locationLabelMapper[location?.state] || dealLocation}</span>
            <span>{industry}</span>
          </Link>
          <ListingPrices
            className={classNames({
              'premium-listing-prices': isListingPremium,
            })}
            askingPrice={askingPriceSelect}
            amount={listing?.amount}
            amountFrom={listing?.amountFrom}
            amountTo={listing?.amountTo}
            revenue={revenue}
            EBITDA={EBITDA}
            averageStockValue={averageStockValue}
            stockAtValue={stockAtValue}
            currency={currency}
          />
        </div>
        <Link
          className="listing-card__content__description"
          to={redirectionListingLink}
        >
          {businessDescription}
        </Link>
        <div className="listing-card__content__actions">
          <div>
            {showEnquire && (
              <Button
                variant="outline"
                onClick={() => onEnquire(listingId, redirectionListingLink)}
                icon={TextBubleIcon}
              >
                {t('enquire')}
              </Button>
            )}
            {(isListingOwner || isEnquired) && (
              <Link
                className="as-button ncoded-button--link"
                to={getPortfolioRoute(
                  !isListingOwner,
                  dealRoomId,
                  listingEnquiryId,
                )}
              >
                <LinkToIcon />
                {t(isPhablet ? 'viewPortfolio' : 'viewInMyPortfolio')}
              </Link>
            )}
            <ResolvePopup
              popupContent={t(
                isListingOwner
                  ? 'thisIsYourListing'
                  : isLoggedIn
                  ? isWatchlisted
                    ? 'removeWatchlistedListing'
                    : 'watchlistListing'
                  : 'loginToWatchlist',
              )}
              onHover
            >
              <Button
                className={classNames('watchlist-button', {
                  'watch-listed': isWatchlisted,
                })}
                variant="link"
                icon={<StarIcon />}
                disabled={isListingOwner || watchlistRequestLoading}
                onClick={onWatchlist}
              >
                {t('shortlist')}
              </Button>
            </ResolvePopup>
            <ShareButton url={getResolveUrl(redirectionListingLink)} />
          </div>
        </div>
      </div>

      <Link
        aria-label="Business details page"
        className="listing-card__gallery-img"
        to={redirectionListingLink}
      >
        <ResolveImage
          src={getImgUrl(gallery[0]?.url, '320')}
          alt="Listing gallery."
          shouldLazyLoad={false}
        />
        {isListingPremium && firmLogo && (
          <ResolveImage
            asCompanyLogo
            alt="Company logo."
            src={getImgUrl(firmLogo, '240')}
            shouldLazyLoad={false}
          />
        )}
      </Link>
    </div>
  );
};

export default ListingCard;
